/**
 * Shared designed states for query-backed tile bodies (UI Quality Contract
 * §"four designed states"): skeleton on first load, then — only when there is
 * NO data at all to render — quiet policy-blocked / disconnected bodies and a
 * destructive-styled error body (visually distinct from "empty": icon + tint
 * + the engine message + what to do — S24 F12). Once a tile has data (even
 * stale), it keeps rendering its children; TileStatusBadge in the frame
 * chrome carries the status. Extracted from ChartTileView (S3) so the S4/S5
 * tile views render identical states.
 *
 * Inside a framed dashboard tile (`TileChromeContext`), the error/policy
 * bodies gain inline recovery actions (`TileStateActions`), and a zero-row
 * TABLE/PIVOT result renders its column headers over the empty copy
 * (`TileEmptyShape`) so the user sees the query ran with the expected shape.
 */
import type { ReactNode } from 'react';
import { CircleAlert, ShieldAlert, Unplug } from 'lucide-react';
import type { UseTileResultValue } from '../../hooks/useTileResult';
import {
  selectTileConfigureHandler,
  useTileConfigureBridgeStore,
} from '../../store/tileConfigureBridgeStore';
import { useTileChrome } from './tileChromeContext';
import { tileEmptyText } from './tileEmptyText';
import { TileEmptyShape } from './TileEmptyShape';
import { TileStateActions } from './TileStateActions';

export function QueryTileStates({
  state,
  children,
}: {
  state: UseTileResultValue;
  children: ReactNode;
}) {
  const chrome = useTileChrome();
  const configureHandler = useTileConfigureBridgeStore(
    selectTileConfigureHandler(chrome?.dashboardId ?? ''),
  );
  const configureAvailable = Boolean(chrome && configureHandler);
  if (state.isFirstLoad) {
    return (
      <div className="flex h-full flex-col gap-2 p-3" data-testid="tile-skeleton" aria-busy="true">
        <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
        <div className="flex-1 animate-pulse rounded bg-muted/60" />
      </div>
    );
  }
  if (!state.result) {
    // No data yet AND the run can't produce any — explain why, quietly.
    // A blocked or disconnected tile is a state, not an error (UI Quality).
    if (state.runtime?.staleReason === 'policy-blocked') {
      return (
        <div
          className="flex h-full flex-col items-center justify-center gap-1.5 px-3 py-2 text-center text-xs text-muted-foreground"
          data-testid="tile-policy-body"
        >
          <ShieldAlert className="h-4 w-4 text-warning" aria-hidden />
          <span>{state.runtime.error}</span>
          {chrome && <TileStateActions variant="policy" />}
        </div>
      );
    }
    if (state.runtime?.staleReason === 'connection-lost') {
      return (
        <div
          className="flex h-full flex-col items-center justify-center gap-1.5 px-3 py-2 text-center text-xs text-muted-foreground"
          data-testid="tile-disconnected-body"
        >
          <Unplug className="h-4 w-4 text-warning" aria-hidden />
          <span>Connection lost — data loads on the next refresh after reconnecting.</span>
        </div>
      );
    }
    if (state.error) {
      // A real failure gets a DESIGNED error state (UI Quality §2): in-frame,
      // destructive-tinted, the engine message (e2e asserts on it), and a
      // recovery path — inline Retry/Configure when the tile is framed, the
      // plain-text hint otherwise. Clamped so long messages can't overflow
      // tiny tiles — the full text stays reachable via the title tooltip.
      return (
        <div
          className="flex h-full min-h-0 flex-col items-center justify-center gap-1 overflow-hidden bg-destructive/5 px-3 py-2 text-center"
          data-testid="tile-error-body"
        >
          <CircleAlert className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
          <span className="line-clamp-3 min-h-0 text-xs font-medium text-destructive" title={state.error}>
            {state.error}
          </span>
          {chrome && <TileStateActions variant="error" />}
          {!configureAvailable && (
            <span className="max-w-full truncate text-[11px] text-muted-foreground">
              Edit the query via the tile menu → Configure
            </span>
          )}
        </div>
      );
    }
  }
  // Ran fine, zero rows, known columns: grid-shaped tiles keep their SHAPE
  // visible (headers + the tile's healthy-empty copy) instead of a bare line.
  const selected = state.result?.selected;
  if (
    chrome &&
    selected &&
    selected.rows.length === 0 &&
    selected.columns.length > 0 &&
    (chrome.tile.tileType === 'table' || chrome.tile.tileType === 'pivot')
  ) {
    return (
      <TileEmptyShape
        columns={selected.columns.map((c) => c.name)}
        copy={tileEmptyText(chrome.tile, 'Query returned no rows')}
      />
    );
  }
  return <>{children}</>;
}
